import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toast } from 'primereact/toast';
import { AuthContext } from '../../../Context/AuthContext';
import { url } from '../../../env';
import { AdminToastContext } from './AdminToastContext';
import { AdminHeader } from './AdminHeader';
import { Sidebar } from './Sidebar';
import { adminShellCSS } from './AdminShell.css';

const titleFromPath = (pathname, routeTitles, fallback = 'Dashboard') => {
    const seg = pathname.split('/').filter(Boolean)[1];
    return routeTitles[seg] || fallback;
};

export const AppShell = ({
    navGroups,
    allowedTypes,
    routeTitles,
    fallbackTitle = 'Dashboard',
    checkPasswordExpiration = false,
    logoHref,
    logoSrc,
}) => {
    const { authInfo } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useRef(null);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    const allowed = useMemo(
        () => allowedTypes.map(String),
        [allowedTypes]
    );

    useEffect(() => {
        setMobileNavOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (authInfo?.loading) return;
        const t = String(authInfo?.user?.type_user ?? '');
        if (!allowed.includes(t)) navigate('/login');
    }, [authInfo, navigate, allowed]);

    useEffect(() => {
        if (!checkPasswordExpiration) return;
        const t = String(authInfo?.user?.type_user ?? '');
        if (!allowed.includes(t) || !authInfo?.user) return;

        const run = async () => {
            try {
                const response = await axios.post(
                    `${url}/autoChangePassword`,
                    {},
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')}`,
                        },
                    }
                );

                const pfData = response.data.personaFisica?.data;
                const pmData = response.data.personaMoral?.data;
                const anyPasswordChanged =
                    pfData?.passwordChanged || pmData?.passwordChanged;

                if (response.data.success && anyPasswordChanged) {
                    const daysRemaining = pfData?.passwordChanged
                        ? pfData?.daysRemaining
                        : pmData?.daysRemaining;
                    const expirationDate = pfData?.passwordChanged
                        ? pfData?.expirationDate
                        : pmData?.expirationDate;

                    toast.current?.show({
                        severity: 'info',
                        summary: 'Contraseña Actualizada Automáticamente',
                        detail: `Se cambió automáticamente la contraseña. Quedan ${daysRemaining} días. Nueva expiración: ${expirationDate}`,
                        life: 8000,
                    });
                } else if (response.data.daysRemaining <= 10) {
                    toast.current?.show({
                        severity: 'warn',
                        summary: 'Contraseña Próxima a Vencer',
                        detail: `Tu contraseña vence en ${response.data.daysRemaining} días. Considera cambiarla pronto.`,
                        life: 5000,
                    });
                }
            } catch (error) {
                console.error('Error verificando expiración de contraseña:', error);
            }
        };

        run();
    }, [authInfo, allowed, checkPasswordExpiration]);

    const pageTitle = titleFromPath(location.pathname, routeTitles, fallbackTitle);

    return (
        <div css={adminShellCSS()}>
            <AdminHeader
                pageTitle={pageTitle}
                onToggleMobileNav={() => setMobileNavOpen((o) => !o)}
                logoHref={logoHref}
                logoSrc={logoSrc}
            />
            <div className="admin-shell-body">
                <Sidebar
                    groups={navGroups}
                    mobileOpen={mobileNavOpen}
                    onMobileClose={() => setMobileNavOpen(false)}
                />
                <main className="admin-shell-main">
                    <AdminToastContext.Provider value={toast}>
                        <Outlet />
                    </AdminToastContext.Provider>
                </main>
            </div>
            <Toast ref={toast} />
        </div>
    );
};

export default AppShell;
